import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Table,
  Space,
  Button,
  Typography,
  Spin,
  Empty,
  message,
  Image as AntImage,
  Card,
  Descriptions,
  Tooltip,
  Modal,
  Row,
  Col,
  Tag,
  Input,
} from "antd";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeftOutlined, CopyOutlined } from "@ant-design/icons";
import { getRequest, postRequest } from "../../util/service";
import { generateImagesFromSmiles } from "../../util/ChemDrawUtils";
import dayjs from "dayjs";
import { FilterCriteriaDisplay } from "../shared/FilterCriteriaDisplay";
import DownloadIconButton from "../shared/DownloadIconButton";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";

const { Title, Text } = Typography;

// Helper to format the pH range from the API data
const formatPhRange = (ligprep) => {
    if (!ligprep) return "N/A";
    const base = ligprep.ph;
    const thr = ligprep.ph_threshold;
    if (base !== undefined && thr !== undefined) return `${base.toFixed(0)} ± ${thr.toFixed(1)}`;
    if (base !== undefined) return String(base.toFixed(0));
    return "N/A";
};

const getStatusProps = (status) => {
  const value = String(status || "");
  const lower = value.toLowerCase();
  if (lower.includes("complete")) return { color: "green", text: value || "Completed" };
  if (lower.includes("fail") || lower.includes("error")) return { color: "red", text: value || "Failed" };
  if (lower.includes("progress")) return { color: "blue", text: value || "In Progress" };
  if (lower.includes("terminate") || lower.includes("cancel")) return { color: "orange", text: value || "Terminated" };
  return { color: "default", text: value || "N/A" };
};

export default function PhaseMolecularDataPage() {
  const navigate = useNavigate();
  const { projectId, batchId, recordId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // ✅ NEW: State to hold all job-level details from the API
  const [jobDetails, setJobDetails] = useState(null);
  const [molecularData, setMolecularData] = useState([]);
  
  const [imgCache, setImgCache] = useState({});
  const inFlightRef = useRef(new Set());
  const [tablePage, setTablePage] = useState(1);
  const pageSize = 10;
  const [isPoseModalVisible, setIsPoseModalVisible] = useState(false);
  const [otherPosesData, setOtherPosesData] = useState([]);
  const [currentHypothesisPdb, setCurrentHypothesisPdb] = useState(null);
  const [searchValue, setSearchValue] = useState(location.state?.prefillMoleculeId || "");

  const fetchMolecularData = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setError("");
    try {
      // ✅ MODIFIED: Fetching all data from the new self-contained endpoint
      const response = await getRequest(`/riva/sd/phase/${recordId}`);
      const data = response?.data?.[0];
      
      if (!data) throw new Error("No data found for this Phase job.");
      
      setJobDetails(data);

      const sorted = (data.phase_molecule_data || []).slice().sort((a, b) => {
        const aHas = Array.isArray(a.phase_molecule_data) && a.phase_molecule_data.length > 1;
        const bHas = Array.isArray(b.phase_molecule_data) && b.phase_molecule_data.length > 1;
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        if (!aHas && !bHas) return 0;
        const aFirst = a.phase_molecule_data?.[1];
        const bFirst = b.phase_molecule_data?.[1];
        const aCount = typeof aFirst?.i_phase_Num_Sites_Matched === "number" ? aFirst.i_phase_Num_Sites_Matched : Number.POSITIVE_INFINITY;
        const bCount = typeof bFirst?.i_phase_Num_Sites_Matched === "number" ? bFirst.i_phase_Num_Sites_Matched : Number.POSITIVE_INFINITY;
        return aCount - bCount;
      });
      setMolecularData(sorted);
    } catch (err) {
      const errorMessage = err.message || "An error occurred while fetching phase data.";
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    fetchMolecularData();
  }, [fetchMolecularData]);

  useEffect(() => {
    setTablePage(1);
  }, [searchValue]);

  // Image Generation Logic (unchanged)
  useEffect(() => {
    let cancelled = false;

    const start = (tablePage - 1) * pageSize;
    const curr = molecularData.slice(start, start + pageSize);
    const next = molecularData.slice(start + pageSize, start + 2 * pageSize); // prefetch next page
    const candidates = [...curr, ...next];

    const toGen = candidates.filter((m) => {
      const id = m?.smile_id;
      return m?.smile_string && !imgCache[id] && !inFlightRef.current.has(id);
    });

    if (!toGen.length) return;

    // mark in-flight to avoid double work while this batch runs
    toGen.forEach((m) => inFlightRef.current.add(m.smile_id));

    (async () => {
      try {
        const urls = await generateImagesFromSmiles(toGen.map((m) => m.smile_string));
        if (cancelled) return;
        const map = {};
        toGen.forEach((m, i) => (map[m.smile_id] = urls[i]));
        setImgCache((prev) => ({ ...prev, ...map }));
      } catch {
        // ignore; table will keep showing <Spin/>
      } finally {
        toGen.forEach((m) => inFlightRef.current.delete(m.smile_id));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [molecularData, tablePage, pageSize, imgCache]);

  const handleCloneJob = () => {
    if (!jobDetails) return;
    navigate(`/project/${projectId}/batch/${batchId}/submit`, {
      state: {
        selectedMolecules: jobDetails.molecule_data.map(m => ({ molecule_id: m.smileId, smile: m.smileString })),
        filterCriteria: jobDetails.filter_criteria,
        jobToClone: {
          selection_name: jobDetails.ms_name,
          selection_desc: jobDetails.ms_description,
          method: "phase",
          ph_range: jobDetails.ligprep_input?.ph,
          threshold: jobDetails.ligprep_input?.ph_threshold,
          phasefile: jobDetails.phase_input?.phase_file,
          selection_id: jobDetails.ms_record_id,
        },
      },
    });
  };

  const handleFileDownload = async (fileName) => {
    if (!fileName) return message.error("No file name specified.");
    message.loading({ content: "Preparing download...", key: "file-download" });
    try {
      const payload = { action: "download", file_name: fileName, file_type: "Phasefiles",  batch_id: batchId, process_id: recordId };
      const response = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, payload);
      if (response?.data?.url) {
        window.location.href = response.data.url;
        message.success({ content: "Download will begin shortly.", key: "file-download", duration: 2 });
      } else {
        throw new Error(response.data.message || "Could not get download link.");
      }
    } catch (error) {
      message.error({ content: error.message || "Download failed.", key: "file-download", duration: 2 });
    }
  };

  const showOtherPoses = (record) => {
    const hypothesisPdb = record.phase_molecule_data?.[0]?.Hypothesis_pdb;
    setCurrentHypothesisPdb(hypothesisPdb);
    const remainingPoses = record.phase_molecule_data?.slice(2) || [];
    setOtherPosesData(remainingPoses);
    setIsPoseModalVisible(true);
  };

  const handleModalClose = () => {
    setIsPoseModalVisible(false);
  };

  const render3dLink = (poseRecord, hypothesisPdb) => {
    return (
      <Link
        to={`/project/${projectId}/batch/${batchId}/molecule3d/${poseRecord.s_m_title}`}
        state={{
          proteinPdbFile: hypothesisPdb,
          ligandPdbFile: poseRecord.pdb_filename,
          phaseId: recordId,
        }}
      >
        View 3D Structure
      </Link>
    );
  };

  const columns = [
    { title: "Molecule ID", dataIndex: "smile_id", key: "moleculeId", width: "15%" },
    {
      title: "2D Structure",
      key: "structure_2d",
      align: "center",
      width: "25%",
      render: (_, record) => {
        const img = imgCache[record.smile_id];
        return (
          <div style={{ width: 190, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {img ? (
                <AntImage src={img} preview={false} style={{ height: 150, width: 150, objectFit: "contain" }} />
              ) : (
                <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Spin />
                </div>
              )}
            </div>
            <div style={{ width: "80%", borderTop: "1px solid #e0e0e0" }} />
            <Tooltip title="Copy SMILES">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => { navigator.clipboard.writeText(record.smile_string || ""); message.success("SMILES Copied!"); }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "No of Matched Sites",
      key: "matchedSitesCount",
      align: "center",
      width: "13%",
      render: (_, record) => {
        const firstSite = record.phase_molecule_data?.[1];
        if (!firstSite) return "N/A";
        const matchedCount = firstSite.i_phase_Num_Sites_Matched;
        const totalCount = (firstSite.s_phase_Matched_Ligand_Sites?.split(" ").length || 0);
        return `${matchedCount}/${totalCount}`;
      },
    },
    {
      title: "Matched Sites",
      key: "matchedSites",
      render: (_, record) => {
        const hypothesisPdb = record.phase_molecule_data?.[0]?.Hypothesis_pdb;
        const firstSite = record.phase_molecule_data?.[1];
        const hasMoreSites = record.phase_molecule_data?.length > 2;

        if (!firstSite) {
          return <Text type="secondary">No match data available.</Text>;
        }

        return (
          <>
            <Space>
              <Text ellipsis={{ tooltip: firstSite.s_phase_Matched_Ligand_Sites }}>
                {firstSite.s_phase_Matched_Ligand_Sites}
              </Text>
              {render3dLink(firstSite, hypothesisPdb)}
            </Space>
            {hasMoreSites && (
              <div style={{ textAlign: "right", marginTop: "8px" }}>
                <Button size="small" type="primary" ghost onClick={() => showOtherPoses(record)}>
                  View {record.phase_molecule_data.length - 2} more site(s)
                </Button>
              </div>
            )}
          </>
        );
      },
    },
  ];

  const modalColumns = [
    { title: "Matched Ligand Sites", dataIndex: "s_phase_Matched_Ligand_Sites", key: "sites", ellipsis: true },
    { title: "3D Structure", key: "action", render: (_, poseRecord) => render3dLink(poseRecord, currentHypothesisPdb) },
  ];

  const filteredData = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return molecularData;
    return molecularData.filter((item) =>
      String(item.smile_id || "").toLowerCase().includes(term)
    );
  }, [searchValue, molecularData]);
  
   if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column", // Stack the spinner and text vertically
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 200px)" // Adjust height to be below the header
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: "16px" }}>Loading Phase Data...</Text>
      </div>
    );
  }
  
  if (error) {
    return <Empty description={error} style={{ marginTop: "48px" }} />;
  }

  return (
    <div style={{ padding: 24, background: "#fff", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, marginTop: 70 }}>
        <Col>
          <Space align="center">
            <Tooltip title="Back to Results">
              <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} />
            </Tooltip>
            <Title level={3} style={{ margin: 0 }}>
             Pharmacophore Result
            </Title>
          </Space>
        </Col>
      </Row>
      <BreadcrumbTrail />
 
      {/* ✅ MODIFIED: Cards now use `jobDetails` state */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card bordered title={<Text strong>Selection Details</Text>} bodyStyle={{ padding: "12px 16px" }}>
            <Descriptions column={1} layout="horizontal" bordered size="small">
              <Descriptions.Item label="Name">{jobDetails?.ms_name}</Descriptions.Item>
              <Descriptions.Item label="Description">{jobDetails?.ms_description}</Descriptions.Item>
              <Descriptions.Item label="Filter Criteria" contentStyle={{ display: "block", overflow: "hidden" }}>
                <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                  <FilterCriteriaDisplay rules={jobDetails?.filter_criteria} />
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card bordered title={<Text strong>User Inputs</Text>} bodyStyle={{ padding: "12px 16px" }}>
            <Descriptions column={1} layout="horizontal" bordered size="small">
              <Descriptions.Item label="Phase File">
                <Space>
                  <span>{jobDetails?.phase_input?.phase_file}</span>
                  <DownloadIconButton
                    title="Download Phase File"
                    disabled={!jobDetails?.phase_input?.phase_file}
                    onClick={() => handleFileDownload(jobDetails?.phase_input?.phase_file)}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="pH Threshold">{formatPhRange(jobDetails?.ligprep_input)}</Descriptions.Item>
              <Descriptions.Item label="Submitted By">{jobDetails?.submitted_id}</Descriptions.Item>
              <Descriptions.Item label="Submitted Time">{dayjs(jobDetails?.submitted_ts).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
              {(jobDetails?.status || jobDetails?.status_message) && (
                <Descriptions.Item label="Status / Message">
                  <Space>
                    <Tag color={getStatusProps(jobDetails?.status).color}>{getStatusProps(jobDetails?.status).text}</Tag>
                    {jobDetails?.status_message && <Text type="secondary">{jobDetails.status_message}</Text>}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Space style={{ width: "100%", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap", rowGap: 12 }}>
        <Input.Search
          placeholder="Search by Molecule ID"
          allowClear
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={(val) => setSearchValue(val)}
          style={{ width: 280 }}
        />
        <Button icon={<CopyOutlined />} onClick={handleCloneJob}>Clone Job</Button>
      </Space>

      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="md_record_id"
        pagination={{ current: tablePage, pageSize, onChange: setTablePage }}
        size="small"
        bordered
        locale={{ emptyText: <Empty description="No molecular data found." /> }}
      />

      <Modal title={"Additional Matched Sites"} open={isPoseModalVisible} onCancel={handleModalClose} footer={[<Button key="close" onClick={handleModalClose}>Close</Button>]} width={800} destroyOnClose>
        <Table bordered dataSource={otherPosesData} columns={modalColumns} rowKey="pdb_filename" pagination={false} size="small" />
      </Modal>
    </div>
  );
}

